import React, { useState } from 'react';
import { Facebook, Instagram, Twitter, Linkedin, Youtube } from 'lucide-react';
import ContactFormModal from './ContactFormModal';
import { useSiteSettings } from '../hooks/useSiteSettings';

const Footer: React.FC = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { data: settings } = useSiteSettings();

  // Default values
  const defaultContactInfo = {
    email: 'services@onarrivalglobal.com',
    phone: '305.204.9837',
    phoneLink: '+13052049837',
  };

  const defaultTagline =
    'Ready to secure your peace of mind? Contact us today to discuss your home watch needs and create a personalized plan for your property.';

  const contactInfo = settings?.footer?.contactInfo || defaultContactInfo;
  const tagline = settings?.footer?.tagline || defaultTagline;
  const socialMedia = settings?.footer?.socialMedia;

  return (
    <>
      <ContactFormModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
      <footer id="contact" className="bg-primary-500 text-white scroll-mt-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-10">
            {/* Logo */}
            <div className="shrink-0">
              <a href="#" aria-label="On Arrival Global Home">
                <img
                  className="h-28 w-auto"
                  src="/OnArrival_Global_Logo.png"
                  alt="On Arrival Global Logo"
                />
              </a>
            </div>

            {/* Contact Info */}
            <div className="text-center md:text-right">
              <h2 className="text-3xl font-bold text-white mb-4">
                Get In Touch
              </h2>
              <p className="max-w-xl mx-auto md:mx-0 md:ml-auto mb-6 text-white/90">
                {tagline}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center md:justify-end gap-6 mb-6 text-lg">
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="font-semibold text-white/90 hover:text-white transition-colors"
                >
                  {contactInfo.email}
                </a>
                <span className="hidden sm:block text-white/60">|</span>
                <a
                  href={`tel:${contactInfo.phoneLink}`}
                  className="font-semibold text-white/90 hover:text-white transition-colors"
                >
                  {contactInfo.phone}
                </a>
              </div>

              {/* Social Media Links */}
              {socialMedia && (
                <div className="flex justify-center md:justify-end gap-4 mb-6">
                  {socialMedia.facebook && (
                    <a
                      href={socialMedia.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                      aria-label="Facebook"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                  )}
                  {socialMedia.instagram && (
                    <a
                      href={socialMedia.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                      aria-label="Instagram"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                  )}
                  {socialMedia.twitter && (
                    <a
                      href={socialMedia.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                      aria-label="Twitter"
                    >
                      <Twitter className="w-5 h-5" />
                    </a>
                  )}
                  {socialMedia.linkedin && (
                    <a
                      href={socialMedia.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                      aria-label="LinkedIn"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                  )}
                  {socialMedia.youtube && (
                    <a
                      href={socialMedia.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                      aria-label="YouTube"
                    >
                      <Youtube className="w-5 h-5" />
                    </a>
                  )}
                </div>
              )}

              {/* Contact Form Button */}
              <div className="flex justify-center md:justify-end">
                <button
                  onClick={() => setIsContactModalOpen(true)}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-white text-primary-500 font-bold text-lg rounded-full hover:bg-sand-100 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 transform"
                >
                  Send Us a Message
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-white/20 pt-8 mt-8 text-center text-sm text-white/80">
            <p>
              &copy; {new Date().getFullYear()} On Arrival Global. All Rights
              Reserved.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
